<script lang="ts" setup>
import { analytics, fileExports } from '@thecompaniesapi/shared/client'

const { getMetaAnalyticsAttributes, getQueryAttributes } = useApiAttributes()
const { t } = useI18n()

const endpoints: ApiEndpoint[] = [
  {
    method: 'GET',
    path: '/companies/analytics/export',
  },
]

const queryAttributes: ApiAttribute[] = [
  {
    description: t('attributes.actionId'),
    name: 'actionId',
    optional: true,
    type: 'number',
  },
  {
    description: t('attributes.attributes'),
    name: 'attributes',
    optional: true,
    type: 'array',
    values: analytics.analyticsAttributes,
  },
  {
    description: t('attributes.format'),
    name: 'format',
    optional: true,
    type: 'string',
    values: fileExports.formats,
  },
  {
    description: t('attributes.full'),
    name: 'full',
    optional: true,
    type: 'boolean',
  },
  {
    description: t('attributes.listId'),
    name: 'listId',
    optional: true,
    type: 'number',
  },
  {
    attributes: getQueryAttributes(),
    description: t('attributes.query'),
    endpoint: '/companies/analytics/export',
    hasSegmentation: true,
    name: 'query',
    optional: true,
    type: 'array',
    warning: t('globals.stringifyAndEncode'),
  },
  {
    description: t('attributes.size'),
    name: 'size',
    optional: true,
    type: 'number',
  },
  {
    description: t('attributes.sort'),
    name: 'sort',
    optional: true,
    type: 'string',
    values: ['asc', 'desc'],
  },
]

const responseAttributes: ApiAttribute[] = [
  {
    description: t('response.data'),
    name: 'data',
    type: 'object',
  },
  getMetaAnalyticsAttributes(),
]
</script>

<template>
  <ApiDoc
    :endpoints="endpoints"
    :loading="false"
    :query-attributes="queryAttributes"
    :response-attributes="responseAttributes"
  />
</template>

<i18n lang="json">
{
  "en": {
    "attributes": {
      "actionId": "The action ID to filter analytics.",
      "attributes": "An array of analytics attributes to export. Required if full is not true.",
      "format": "The format of the exported file (defaults to json).",
      "full": "Whether to export all analytics attributes.",
      "listId": "Filter analytics to a specific list.",
      "query": "An array of conditions to filter the companies.",
      "size": "The number of results to return.",
      "sort": "The sort order of the results."
    },
    "response": {
      "data": "An array of analytics data points containing counts and percentages for each value",
      "meta": "Metadata about the analytics results, including total documents, datapoints, and applied filters"
    }
  },
  "es": {
    "attributes": {
      "actionId": "El ID de la acción para filtrar los análisis.",
      "attributes": "Una matriz de atributos de análisis para exportar. Requerido si full no es verdadero.",
      "format": "El formato del archivo exportado (por defecto json).",
      "full": "Si se deben exportar todos los atributos de análisis.",
      "listId": "Filtrar análisis a lista específica.",
      "query": "Una matriz de condiciones para filtrar las empresas.",
      "size": "Le nombre de résultats à retourner.",
      "sort": "L'ordre de tri des résultats."
    },
    "response": {
      "data": "Un array de puntos de datos de análisis que contienen conteos y porcentajes para cada valor",
      "meta": "Metadatos sobre los resultados de los análisis, incluyendo documentos totales, puntos de datos y filtros aplicados"
    }
  },
  "fr": {
    "attributes": {
      "actionId": "L'ID de l'action pour filtrer les analyses.",
      "attributes": "Un tableau d'attributs d'analyse à exporter. Requis si full n'est pas vrai.",
      "format": "Le format du fichier exporté (par défaut json).",
      "full": "Si tous les attributs d'analyse doivent être exportés.",
      "listId": "Filtrer les analyses pour une liste spécifique.",
      "query": "Un tableau de conditions pour filtrer les entreprises.",
      "size": "Le nombre de résultats à retourner.",
      "sort": "L'ordre de tri des résultats."
    },
    "response": {
      "data": "Un tableau de points de données d'analyse contenant des comptes et des pourcentages pour chaque valeur",
      "meta": "Métadonnées sur les résultats des analyses, y compris le nombre total de documents, de points de données et de filtres appliqués"
    }
  }
}
</i18n>
